import { DocumentNode, QueryResult, useApolloClient, useQuery } from '@apollo/client';
import { createContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import {
  Maybe,
  me,
  PermissionsEnum,
  ProcureUser,
  USER_INVITATION_STATUS,
  USER_STATUS,
  UserInvitation,
  UserPermission,
  WorkOrder,
} from 'vendor-marketplace-shared-library';

export interface AuthParams {
  userQuery: QueryResult;
  accessToken: string | null;
  login: (userId: number, accessToken: string) => Promise<void>;
  logout: () => void;
  isOnboarded: boolean;
  loading: boolean;
  forcePropertyPageOnboarding: boolean;
  setForcePropertyPageOnboarding: (value: boolean) => void;
  validateUserPermissions: (permissions: PermissionsEnum[]) => boolean;
  validateWorkOrderUserPermissions: (workOrder: WorkOrder, permission: PermissionsEnum) => boolean;
}

export const AuthContext = createContext<AuthParams>({
  userQuery: {} as QueryResult,
  accessToken: null,
  login: async (userId: number, accessToken: string) => {},
  logout: () => {},
  isOnboarded: false,
  loading: false,
  forcePropertyPageOnboarding: false,
  setForcePropertyPageOnboarding: (value: boolean) => {},
  validateUserPermissions: (permissions: PermissionsEnum[]) => false,
  validateWorkOrderUserPermissions: (workOrder: WorkOrder, permission: PermissionsEnum) => false,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const client = useApolloClient();
  const [cookies, setCookie, removeCookie] = useCookies(['access_token']);
  const [loading, setLoading] = useState(false);
  const [intervalId, setIntervalId] = useState<any>(null);

  // This state is used to force the user to the property page onboarding due to user query refetch every 15 seconds
  const [forcePropertyPageOnboarding, setForcePropertyPageOnboarding] = useState(false);

  const login = async (userId: number, accessToken: string) => {
    try {
      setLoading(true);
      setCookie('access_token', accessToken, {
        path: '/',
        sameSite: 'strict',
        maxAge: 24 * 60 * 60 * 30,
      }); // Cookie expires after 30 days
      await client.clearStore(); // use of .resetStore() causes an error when switching user permissions, potentially due to in-flight gql queries
      await userQuery.refetch();
      setLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  const logout = () => {
    removeCookie('access_token');
    client.resetStore();
  };

  const userQuery = useQuery(me, {
    skip: !cookies.access_token,
    pollInterval: 15000,
    fetchPolicy: 'cache-and-network',
    onCompleted: () => {
      // cancel the interval on complete
      if (intervalId) {
        clearInterval(intervalId);
      }
      setLoading(false);
    },
    onError: () => {
      // Refetch the user query at a higher rate than the default polling
      if (!intervalId) {
        const intervalId = setInterval(() => {
          client.refetchQueries({ include: ['me'] });
        }, 2500);
        setIntervalId(intervalId);
      }
    },
  });

  const getPermissionNames = (user: ProcureUser): string[] => {
    if (!user || !user.userRole || !user.userRole.userPermissions) {
      return [];
    }
    return user.userRole.userPermissions.map(
      (permission: Maybe<UserPermission>) => permission!.name,
    );
  };

  const validateUserPermissions = (permissions: PermissionsEnum[]): boolean => {
    const user: ProcureUser = userQuery.data?.me;
    const userPermissionNames: string[] = getPermissionNames(user);
    return permissions.every((permission: PermissionsEnum) =>
      userPermissionNames.includes(permission),
    );
  };

  const validateWorkOrderUserPermissions = (
    workOrder: WorkOrder,
    permission: PermissionsEnum,
  ): boolean => {
    if (!workOrder) return false;

    const user: ProcureUser = userQuery.data?.me;

    if (getPermissionNames(user).includes(permission)) {
      return true;
    } else if (
      permission === PermissionsEnum.WORK_ORDERS_DELETE ||
      permission === PermissionsEnum.WORK_ORDER_DETAILS_UPDATE ||
      permission === PermissionsEnum.WORK_ORDER_STATUS_UPDATE ||
      permission === PermissionsEnum.WORK_ORDER_ASSIGNEE_UPDATE ||
      permission === PermissionsEnum.WORK_ORDER_PRIORITY_UPDATE
    ) {
      return workOrder?.assignedUser?.id === user.id || workOrder.createdByUser?.id === user.userId;
    } else if (
      permission === PermissionsEnum.WORK_ORDER_ATTACHMENTS_UPDATE ||
      permission === PermissionsEnum.WORK_ORDER_ATTACHMENTS_DELETE
    ) {
      return workOrder?.assignedUser?.id === user.id;
    }
    return false;
  };

  const hasActiveStatus = userQuery.data?.me.status === USER_STATUS.ACTIVE;
  const hasActiveInvitations = userQuery.data?.me?.phoneContactMethod?.userInvitations?.some(
    (invitation: UserInvitation) =>
      [USER_INVITATION_STATUS.CREATED, USER_INVITATION_STATUS.SENT].includes(
        invitation.inviteStatus!,
      ),
  );
  const hasCreatedOrganizationAccount = userQuery.data?.me.organizationAccount;
  const hasCreatedProperties = userQuery.data?.me.properties?.length;

  const isOnboarded =
    hasActiveStatus &&
    !!hasCreatedOrganizationAccount &&
    !!hasCreatedProperties &&
    !hasActiveInvitations &&
    !forcePropertyPageOnboarding;

  return (
    <AuthContext.Provider
      value={{
        userQuery,
        accessToken: cookies.access_token,
        login,
        logout,
        isOnboarded,
        loading,
        forcePropertyPageOnboarding,
        setForcePropertyPageOnboarding,
        validateUserPermissions,
        validateWorkOrderUserPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
